import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import {ActivityIndicator, Dimensions, InteractionManager, Keyboard, KeyboardAvoidingView, Platform, StyleSheet, useWindowDimensions, View} from "react-native";
import Icon from 'react-native-vector-icons/FontAwesome';
import IonIcon from 'react-native-vector-icons/Ionicons';
import SaleScreen from "./SaleScreen";
import CartScreen from "./CartScreen";
import {CartContext} from '../helpers/CartContext';
import BigSaleScreen from './BigSaleScreen';
import SuccessModal from "./SuccessModal";
import {useNavigation, useRoute} from "@react-navigation/native";
import FailModal from "./FailModal";
import FunctionsScreen from "./FunctionsScreen";
import {TableContext} from "../helpers/TableContext";
import CircularButton from '../components/CircularButton';
import {usePosPhoneTabBar} from '../components/pos/usePosPhoneTabBar';
import AsyncStorage from "@react-native-async-storage/async-storage";
import safeJsonParse from "../helpers/safeJsonParse";
import {SystemContext} from "../helpers/SystemContext";
import {VipCardContext} from "../helpers/VipCardContext";
import {OnlineOrderContext} from "../helpers/OnlineOrderContext";
import OrdersScreen from "./OrdersScreen";
import {OrdersContext} from "../helpers/OrdersContext";
import StatisticsHelper from "../helpers/StatisticsHelper";
import {useTranslation} from 'react-i18next';
import '../../i18n';
import Observables from "@happy-gastro/global-observables";
import CustomTooltip from "../helpers/CustomTooltip";
import {TourContext} from "../helpers/TourContext";
import PushNotificationsBootstrap from '../components/PushNotificationsBootstrap';
import TransportStatusBadge from '../components/TransportStatusBadge';
import {useLastTab} from '../helpers/useLastTab';
import {useBottomInset} from '../helpers/safeArea';
import {screenKavBehavior} from '../components/KeyboardAware';
import {useTheme} from '../helpers/ThemeContext';

const Tab = createBottomTabNavigator();

export default function HomeScreen({ }) {
    const { t } = useTranslation();
    const { theme } = useTheme();
    // Responsive width: re-evaluated on resize so the Sales tab registration
    // (Sale vs BigSale) always matches the current viewport. A stale module-level
    // Dimensions snapshot used to register 'BigSale' while the bottom-nav link
    // still pointed at '/HomeScreen/Sale', so tapping Sales did nothing (SVC-002).
    const { width } = useWindowDimensions();
    const { currentTable, removeTable } = useContext(TableContext);
    const { cartItems = [], paymentModalVisible = false, setCart = () => { }, setPaymentModalVisible = () => { }, setAlreadySentCartItems = () => { }, alreadySentCartItems = [], setDiscountsForWholeCart = () => { } } = useContext(CartContext) || {};
    const route = useRoute();
    const { setSelectedCategory, setSelectedEvent, fetchSystemData } = useContext(SystemContext);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [failModalVisible, setFailModalVisible] = useState(false);
    const [order, setOrder] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const { removeVipCard, removeEmployee } = useContext(VipCardContext);
    const { resetOnlineOrder } = useContext(OnlineOrderContext);
    const { orders, activeTransport = 'polling' } = useContext(OrdersContext);
    const { productsSum, statistics } = useMemo(() => StatisticsHelper(orders), [orders]);
    const navigation = useNavigation();
    const {ready: lastTabReady, initialRouteName: lastTab, screenListeners: tabListeners} = useLastTab('salesHome', {
        fallback: 'Sale',
        valid: ['Sale', 'Cart', 'BigSale', 'HomeOrdersList', 'Functions'],
    });
    const [restaurantPlace, setRestaurantPlace] = useState({ _id: '' });
    const { currentStep, endTour, nextStep, activeAppTutorialStep } = useContext(TourContext);
    // Phone: the order-type button sits in the bar's centre slot (guided-tour step 11 wraps it).
    const wrapFab = useCallback((fab) => (
        <CustomTooltip
            isVisible={currentStep === 11}
            onNext={() => {
                setCart([])
                endTour();
            }}
            bottomMargin={80}
            placement="top"
            module={"default"}
            onClose={endTour}
            wrapped={fab}
        />
    ), [currentStep, setCart, endTour]);
    const { isPhone: isPosPhone, tabBar: phoneTabBar } = usePosPhoneTabBar(navigation, wrapFab);
    // Your existing state and context variables...
    const [isKeyboardVisible, setKeyboardVisible] = useState(false); // Add this state
    const bottomInset = useBottomInset(); // Clear the Android nav bar / iOS home indicator

    // Add this useEffect for keyboard listeners
    useEffect(() => {
        const keyboardDidShowListener = Keyboard.addListener(
            'keyboardDidShow',
            () => {
                setKeyboardVisible(true);
            }
        );

        const keyboardDidHideListener = Keyboard.addListener(
            'keyboardDidHide',
            () => {
                setKeyboardVisible(false);
            }
        );

        // Clean up listeners
        return () => {
            keyboardDidShowListener.remove();
            keyboardDidHideListener.remove();
        };
    }, []);

    const loggedIn = route.params?.loggedIn || false;

    useEffect(() => {
        const fetchRestaurantPlace = async () => {
            const restaurantPlaceStored = await AsyncStorage.getItem("restaurantPlace");
            const parsedRestaurantPlace = safeJsonParse(restaurantPlaceStored);
            if (parsedRestaurantPlace) {
                setRestaurantPlace(parsedRestaurantPlace);
            }
        };
        
        fetchRestaurantPlace();
        
        // Defer system data fetch until animations complete
        InteractionManager.runAfterInteractions(() => {
            fetchSystemData();
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        let refreshTimer;

        if (loggedIn) {
            refreshTimer = globalThis.setTimeout(() => Observables.call('fetchSystemData'), 1000);
        }
        if (route.params?.saleScreen && width > 768) navigation.navigate('BigSale', { ...route.params });
        if (route.params?.saleScreen && width < 768) navigation.navigate('Cart', { ...route.params });

        return () => {
            if (refreshTimer) {
                globalThis.clearTimeout(refreshTimer);
            }
        };
    }, [loggedIn, navigation, route.params]);

    useEffect(() => {
        if (route.params?.userInfo) {
            setUserInfo(route.params.userInfo);
        }
    }, [route.params]);

    useEffect(() => {
        const requestedTab = activeAppTutorialStep?.homeTabName;

        if (!requestedTab) {
            return;
        }

        const resolvedTab = width >= 768 && (requestedTab === 'Sale' || requestedTab === 'Cart')
            ? 'BigSale'
            : requestedTab;

        navigation.navigate(resolvedTab);
    }, [activeAppTutorialStep?.homeTabName, navigation]);

    useEffect(() => {
        if (route.params?.paymentSuccess !== undefined) {
            if (route.params?.paymentSuccess === true) {
                setOrder(route?.params?.orderResponse)
                setSuccessModalVisible(true);
                setPaymentModalVisible(false);
                navigation.navigate(width >= 768 ? 'BigSale' : 'Sale');
            } else {
                setFailModalVisible(true);
                setPaymentModalVisible(false);
            }
        }
    }, [route.params]);

    const handleCloseModal = useCallback(() => {
        setSuccessModalVisible(false);
        setFailModalVisible(false);
        navigation.setParams({ paymentSuccess: undefined });
    }, [navigation]);

    const getRemainingItems = useCallback((tableOrderItems, payedItems) => {
        // Create a copy of tableOrderItems to avoid mutating the original array
        const remainingItems = [...tableOrderItems];

        // Iterate through each payedItem
        payedItems.forEach(payedItem => {
            // Find the corresponding item in remainingItems
            const itemIndex = remainingItems.findIndex(item => item._id === payedItem._id);

            if (itemIndex !== -1) {
                // Reduce the amount in the corresponding item
                remainingItems[itemIndex].amount -= payedItem.amount;

                // If the amount becomes 0 or less, remove the item from the array
                if (remainingItems[itemIndex].amount <= 0) {
                    remainingItems.splice(itemIndex, 1);
                }
            }
        });

        return remainingItems;
    }, []);

    const handleCloseSuccessModal = useCallback(async () => {
        setSuccessModalVisible(false);
        setFailModalVisible(false);
        navigation.setParams({ paymentSuccess: undefined });
        Observables.call("reloadOrders");
        Observables.call("refreshOrders");
        if (currentTable) {
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            if (tableOrder?.items && tableOrder?.items.length > 0) {
                //navigation.navigate('HomeScreen');
                //console.log("close params:", route?.params)
                fetchSystemData();
                removeVipCard();
                removeEmployee();
                resetOnlineOrder();

                setAlreadySentCartItems(tableOrder.items)

            } else {
                navigation.navigate('TablesScreen');
                removeTable();
                setAlreadySentCartItems([]);
                setSelectedCategory(null);
                setSelectedEvent(null);
                await AsyncStorage.removeItem('currentTable');
                setDiscountsForWholeCart({ amount: 0, percentage: 0 });
                removeVipCard();
                removeEmployee();
                fetchSystemData();
                resetOnlineOrder();
            }
        } else {
            removeTable();
            setAlreadySentCartItems([]);
            setSelectedCategory(null);
            setSelectedEvent(null);
            await AsyncStorage.removeItem('currentTable');
            setDiscountsForWholeCart({ amount: 0, percentage: 0 });
            removeVipCard();
            removeEmployee();
            resetOnlineOrder();
            fetchSystemData();
            resetOnlineOrder();
        }
    }, [
        currentTable,
        orders,
        navigation,
        fetchSystemData,
        removeVipCard,
        removeEmployee,
        resetOnlineOrder,
        setAlreadySentCartItems,
        removeTable,
        setSelectedCategory,
        setSelectedEvent,
        setDiscountsForWholeCart
    ]);
    // Memoize the screen options to prevent unnecessary re-renders
    const screenOptions = useMemo(() => ({
        headerShown: false,
        // Use newer screenOptions API instead of deprecated tabBarOptions
        tabBarActiveTintColor: theme.colors.primary,
        tabBarInactiveTintColor: theme.colors.subText,
        tabBarActiveBackgroundColor: theme.colors.inputBackground,
        tabBarInactiveBackgroundColor: theme.colors.surface,
        tabBarShowLabel: true,
        tabBarLabelStyle: {
            fontSize: 14,
            fontWeight: 'bold',
        },
        tabBarStyle: {
            height: 62 + bottomInset,
            backgroundColor: theme.colors.card,
            paddingBottom: bottomInset,
            marginBottom: 0,
            display: isKeyboardVisible ? 'none' : 'flex' // This hides the tab bar when keyboard is visible
        },
        tabBarItemStyle: {
            paddingVertical: 6,
            height: 62,
        },
        sceneStyle: {
            minHeight: 0,
        },
        // Improve animation performance
        animationEnabled: false,
    }), [isKeyboardVisible, bottomInset, theme]);

    // Memoize tab icons to prevent recreation on each render
    const homeIcon = useCallback(({ color, size }) => <Icon name="home" color={color} size={size} />, []);
    const basketIcon = useCallback(({ color, size }) => <Icon name="shopping-basket" color={color} size={size} />, []);
    const bagIcon = useCallback(({ color, size }) => (
        <CustomTooltip
            isVisible={currentStep === 9}
            onNext={() => nextStep()}
            placement="top"
            module={"default"}
            onClose={endTour}
            wrapped={<IonIcon name="bag-sharp" color={color} size={size} />}
        />
    ), [currentStep, nextStep, endTour]);

    const serverIcon = useCallback(({ color, size }) => (
        <CustomTooltip
            isVisible={currentStep === 10}
            onNext={() => nextStep()}
            placement="top"
            module={"default"}
            onClose={endTour}
            wrapped={<Icon name="server" color={color} size={size} />}
        />
    ), [currentStep, nextStep, endTour]);

    const shownOrders = useMemo(() =>
        [...statistics.onlineOrders, ...statistics.unClosedTakeAwayOrders, ...statistics.dineInOrders]
            .filter(order => !order.storno || order.status !== 'closed' || order.status !== 'cancelled'),
        [statistics.onlineOrders, statistics.unClosedTakeAwayOrders, statistics.dineInOrders]
    );

    // NOTE: This must stay a top-level hook. Previously it lived inside the JSX
    // (nested under the `!lastTabReady` branch), so the hook count changed once
    // the last-tab state resolved -> "Rendered more hooks than during the previous
    // render" crash that tripped the ErrorBoundary and killed the Sales module.
    const ordersBadge = useMemo(() => {
        const ordersBadgeCount = shownOrders?.filter(order => order.restaurantPlace === restaurantPlace?._id)?.length || 0;
        const ordersBadgeStyle = ordersBadgeCount === 0
            ? { display: 'none' }
            : { minWidth: "fit-content", height: "fit-content", borderRadius: 14, justifyContent: 'center', alignItems: 'center', overflow: "show" };
        return { ordersBadgeCount, ordersBadgeStyle };
    }, [shownOrders, restaurantPlace?._id]);


    return (
        // behavior MUST stay `screenKavBehavior` (= undefined on Android).
        //
        // This avoider wraps the whole sales Tab.Navigator, so anything it does
        // to its own height moves every pane and the tab bar with it. With
        // behavior="height" React Native renders
        // `{height: _initialFrameHeight - bottom, flex: 0}` and re-derives
        // `bottom` from the view's own onLayout frame — the view shrinks, that
        // fires onLayout, the new frame yields a different `bottom`, and it
        // never converges. Our activity is `windowSoftInputMode="adjustPan"`
        // (AndroidManifest) / `softwareKeyboardLayoutMode: "pan"` (app.json), so
        // the window does not resize with the keyboard and the avoider's own
        // shrink is the only thing moving the frame: an undamped feedback loop
        // that kept ticking long after the keyboard closed (opening the product
        // search, then adding to cart, left the whole screen flickering).
        //
        // Every other full-screen avoider in the app already uses `undefined` on
        // Android — see the rationale in components/KeyboardAware.js. `height`
        // is only correct inside a RN <Modal> (`modalKavBehavior`), which
        // renders in its own window and does not inherit the activity's
        // soft-input mode.
        <KeyboardAvoidingView
            style={{ flex: 1, minHeight: 0 }}
            behavior={screenKavBehavior}
        >
            <View style={{ flex: 1, width: '100%', height: '100%', flexGrow: 1, minHeight: 0 }}>
                {!isKeyboardVisible && (
                    <View style={styles.transportBadgeWrap}>
                        <TransportStatusBadge transport={activeTransport} compact />
                    </View>
                )}
                <PushNotificationsBootstrap navigation={navigation} />
                {!lastTabReady ? (
                    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <ActivityIndicator size="large" />
                    </View>
                ) : (
                <Tab.Navigator
                    initialRouteName={(() => {
                        // Keep the restored tab valid for the current width: at >=768 only
                        // 'BigSale' is registered, so map a stored 'Sale'/'Cart' onto it.
                        const restored = lastTab || (width >= 768 ? 'BigSale' : 'Sale');
                        if (width >= 768 && (restored === 'Sale' || restored === 'Cart')) return 'BigSale';
                        if (width < 768 && restored === 'BigSale') return 'Sale';
                        return restored;
                    })()}
                    screenListeners={tabListeners}
                    detachInactiveScreens={true} // Changed to true for better performance
                    tabBarLabelPosition={"below-icon"}
                    lazy={true} // Changed to true for better initial load performance
                    backBehavior={"firstRoute"}
                    swipeEnabled={false} // Disable swipe for better performance
                    tabBar={phoneTabBar}
                    screenOptions={screenOptions}
                >
                    {width < 768 ? (
                        <>
                            <Tab.Screen name="Sale" component={SaleScreen}
                                options={{
                                    tabBarIcon: homeIcon,
                                    title: t('sales'),
                                    tabBarLabel: t('sales'),
                                }} />
                            <Tab.Screen name="Cart" component={CartScreen}
                                options={{
                                    tabBarIcon: basketIcon,
                                    title: t('cart'),
                                    tabBarLabel: t('cart'),
                                    ...((cartItems.length > 0) && {
                                        tabBarBadge: cartItems.length || 0,
                                        tabBarBadgeStyle: {
                                            minWidth: "fit-content",
                                            height: "fit-content",
                                            borderRadius: 14,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            overflow: "show"
                                        }
                                    }),
                                }} />
                        </>
                    ) : (
                        <Tab.Screen name="BigSale" component={BigSaleScreen}
                            options={{
                                tabBarIcon: homeIcon,
                                title: t('sales'),
                                tabBarLabel: t('sales'),
                                numColumns: 3,
                            }} />
                    )}

                    {/* Badge count is memoized at the top level (ordersBadge) */}
                    <Tab.Screen name="HomeOrdersList" component={OrdersScreen}
                        options={{
                            tabBarIcon: bagIcon,
                            title: t('open_orders'),
                            tabBarLabel: t('open_orders'),
                            tabBarBadge: ordersBadge.ordersBadgeCount,
                            tabBarBadgeStyle: ordersBadge.ordersBadgeStyle
                        }}
                        initialParams={{ name: "OrdersList" }}
                    />

                    <Tab.Screen name="Functions" component={FunctionsScreen}
                        options={{
                            tabBarIcon: serverIcon,
                            title: t('functions'),
                            tabBarLabel: t('functions'),
                        }} />
                </Tab.Navigator>
                )}
                {!isKeyboardVisible && !isPosPhone && (
                    <CustomTooltip
                        isVisible={currentStep === 11}
                        onNext={() => {
                            setCart([])
                            endTour();
                        }}
                        bottomMargin={80}
                        placement="top"
                        module={"default"}
                        onClose={endTour}
                        wrapped={<CircularButton navigation={navigation} iconName="grid" />}
                    />
                )}


                <SuccessModal visible={successModalVisible} order={order} onClose={handleCloseSuccessModal} />
                <FailModal visible={failModalVisible} onClose={handleCloseModal} />
            </View>
        </KeyboardAvoidingView>
    );
}

const styles = StyleSheet.create({
    transportBadgeWrap: {
        position: 'absolute',
        top: 10,
        right: 12,
        zIndex: 20,
    },
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
